import Compressor from 'compressorjs'
import 'cropperjs/dist/cropper.css'
import { isEmpty } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import Cropper from 'react-cropper'

import { Box, Button, Dialog, Flex, Portal, SegmentGroup, Text } from '@chakra-ui/react'
import { toaster } from 'uikit/Toaster'
import { DropZoneImage } from '../DropZoneImage'

export const ImageCropper = ({
  title,
  isOpen,
  file,
  colorPalette = 'blue',
  onClose,
  onChange,
  isLoading,
  isCropRequired,
  optionsCroppedCanvas,
  optionsCropper,
  optionsCompress,
}) => {
  const containerTab = useRef()
  const [activeTab, setActiveTab] = useState(0)
  const [fileState, setFileState] = useState(null)
  const [isCropped, setIsCropped] = useState(false)
  const cropperRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    if (!isEmpty(file)) {
      setFileState(file)
    } else {
      setFileState(null)
    }
    setActiveTab(0)
  }, [file, isOpen])

  const getCropData = () => {
    const imageElement = cropperRef?.current
    const cropper = imageElement?.cropper
    const result = {
      name: fileState.name,
      type: fileState.type,
      uri: cropper
        .getCroppedCanvas({
          fillColor: '#fff',
          imageSmoothingEnabled: false,
          imageSmoothingQuality: 'high',
          ...optionsCroppedCanvas,
        })
        .toDataURL(),
    }
    setIsCropped(true)
    setFileState(result)

    return result
  }

  const handleUpload = (acceptedFiles) => {
    const uploadFile = acceptedFiles[0]
    new Compressor(uploadFile, {
      ...optionsCompress,
      success(result) {
        const reader = new FileReader()
        reader.readAsDataURL(result)
        reader.onloadend = () => {
          setIsCropped(false)
          setFileState({
            name: uploadFile.name,
            type: uploadFile.type,
            uri: reader.result,
          })
          if (isCropRequired) {
            setActiveTab(1)
          }
        }
      },
      error(err) {
        console.error(err)
        toaster.create({
          description: `Ошибка конвертирования`,
          variant: 'solid',
          type: 'error',
        })
      },
    })
  }

  const handleClose = () => {
    onClose && onClose()
  }

  const handleSave = () => {
    if (isCropRequired && activeTab === 0) {
      toaster.create({
        description: `Отредактируйте изображение, чтобы сохранить`,
        variant: 'solid',
        type: 'warning',
      })
      return
    }

    if (activeTab === 1) {
      const cropData = getCropData()
      if (cropData?.uri.slice(0, 4) === 'data') {
        onChange && onChange(cropData)
      }
    } else if (fileState?.uri.slice(0, 4) === 'data') {
      onChange && onChange(fileState)
    }

    handleClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => (e.open ? null : handleClose())}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="576px">
            <Dialog.Header>
              <Dialog.Title>{title ? title : 'Загрузка изображения'}</Dialog.Title>
              <Dialog.CloseTrigger mt={1} />
            </Dialog.Header>
            <Dialog.Body>
              <Box ref={containerTab}>
                {!!fileState?.uri && (
                  <SegmentGroup.Root
                    mb={6}
                    w="full"
                    size="sm"
                    value={String(activeTab)}
                    defaultValue={String(activeTab)}
                    onValueChange={(e) => setActiveTab(e.value)}
                  >
                    <SegmentGroup.Indicator />
                    <SegmentGroup.Items
                      flexGrow={1}
                      cursor="pointer"
                      items={[
                        {
                          label: 'Загрузка',
                          value: 0,
                        },
                        {
                          label: 'Обрезка',
                          value: 1,
                        },
                      ]}
                    />
                  </SegmentGroup.Root>
                )}
              </Box>
              {activeTab === 0 && (
                <DropZoneImage
                  maxFiles={1}
                  maxSize={4194304}
                  height="528px"
                  width="528px"
                  multiple={false}
                  uploadedImage={fileState?.uri}
                  onUpload={handleUpload}
                  onError={() => {
                    toaster.create({
                      description: `Изображение слишком большое`,
                      variant: 'solid',
                      type: 'error',
                    })
                  }}
                />
              )}
              {activeTab === 1 && !isEmpty(fileState) && (
                <Cropper
                  src={fileState?.uri}
                  style={{ height: 528, width: 528 }}
                  guides={false}
                  zoomable={false}
                  checkCrossOrigin={false}
                  ref={cropperRef}
                  {...optionsCropper}
                />
              )}
            </Dialog.Body>
            <Dialog.Footer paddingBottom="24px" justifyContent="space-between">
              <Flex>
                {activeTab === 1 && isCropRequired && !isCropped && (
                  <Text fontSize="0.8rem" color={`${colorPalette}.500`}>
                    Отредактируйте изображение
                  </Text>
                )}
              </Flex>
              <Box>
                <Button onClick={handleClose} mr={3}>
                  Закрыть
                </Button>
                <Button
                  colorPalette={colorPalette}
                  onClick={handleSave}
                  disabled={!fileState?.uri}
                  loading={isLoading}
                >
                  {activeTab === 0 ? 'Сохранить' : 'Обрезать и сохранить'}
                </Button>
              </Box>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
